import React from 'react';
import { motion } from 'framer-motion';
import { DownloadIcon, LockIcon, WrenchIcon, PlusIcon } from 'lucide-react';
import { SectionHeading } from '../components/SectionHeading';
import { RESOURCES, ResourceItem } from '../data/site';
interface ResourcesPreviewProps {
  onOpenResource: (title: string) => void;
}
function StatusBadge({
  status
}: {
  status: ResourceItem['status'];
}) {
  const map = {
    'Free download': {
      cls: 'bg-improvements/15 text-improvements',
      Icon: DownloadIcon
    },
    'Coming soon': {
      cls: 'bg-charcoal/8 text-warmgray',
      Icon: LockIcon
    },
    'Framework tool': {
      cls: 'bg-decisions/15 text-decisions',
      Icon: WrenchIcon
    }
  }[status];
  const Icon = map.Icon;
  return <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-bold ${map.cls}`} data-id="element-622">
      <Icon className="h-3 w-3" data-id="element-623" />
      {status}
    </span>;
}
export function ResourcesPreview({
  onOpenResource
}: ResourcesPreviewProps) {
  return <section id="resources" className="w-full scroll-mt-20 bg-sand py-20 lg:py-28" data-id="element-624">
      <div className="mx-auto max-w-7xl px-5 lg:px-8" data-id="element-625">
        <SectionHeading eyebrow="Resources" title="Free tools for better mine geology systems." body="Practical checklists, prompt sheets and templates — drawn straight from the framework. Some are ready now; more are on the way." data-id="element-626" />

        <div className="mt-12 grid items-stretch gap-6 sm:grid-cols-2 lg:grid-cols-3" data-id="element-627">
          {RESOURCES.map((r, i) => {
          const available = r.status !== 'Coming soon';
          return <motion.div key={r.title} initial={{
            opacity: 0,
            y: 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-60px'
          }} transition={{
            duration: 0.4,
            delay: i * 0.05
          }} className="flex h-full flex-col rounded-xl border border-charcoal/10 bg-cream p-6 shadow-card transition-transform duration-200 hover:-translate-y-1" data-id="element-628">
                <div className="flex items-center justify-between" data-id="element-629">
                  <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide" style={{
                color: r.categoryColor
              }} data-id="element-630">
                    <span className="h-2 w-2 rounded-sm" style={{
                  backgroundColor: r.categoryColor
                }} data-id="element-631" />
                    {r.category}
                  </span>
                  <StatusBadge status={r.status} data-id="element-632" />
                </div>
                <h3 className="mt-4 text-lg font-bold leading-snug text-charcoal" data-id="element-633">
                  {r.title}
                </h3>
                <p className="mt-2 flex-1 text-sm leading-relaxed text-warmgray" data-id="element-634">
                  {r.description}
                </p>
                <button type="button" disabled={!available} onClick={() => onOpenResource(r.title)} className={`mt-auto inline-flex items-center justify-center gap-2 rounded-md px-4 py-2.5 text-sm font-bold transition-colors ${available ? 'bg-yellow text-charcoal hover:brightness-95' : 'cursor-not-allowed bg-charcoal/8 text-warmgray'}`} data-id="element-635">
                  {available ? <>
                      <DownloadIcon className="h-4 w-4" data-id="element-636" />
                      Download
                    </> : 'Coming soon'}
                </button>
              </motion.div>;
        })}

          <motion.a href="#contact" initial={{
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-60px'
        }} transition={{
          duration: 0.4,
          delay: RESOURCES.length * 0.05
        }} className="flex h-full flex-col rounded-xl border-2 border-dashed border-charcoal/20 bg-cream p-6 shadow-card transition-transform duration-200 hover:-translate-y-1" data-id="element-637">
            <div className="flex items-center justify-between" data-id="element-638">
              <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-warmgray" data-id="element-639">
                <span className="h-2 w-2 rounded-sm bg-yellow" data-id="element-640" />
                Your input
              </span>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-yellow/20 px-2.5 py-1 text-xs font-bold text-charcoal" data-id="element-641">
                <PlusIcon className="h-3 w-3" data-id="element-642" />
                Suggest
              </span>
            </div>
            <h3 className="mt-4 text-lg font-bold leading-snug text-charcoal" data-id="element-643">
              More resources coming soon
            </h3>
            <p className="mt-2 flex-1 text-sm leading-relaxed text-warmgray" data-id="element-644">
              Need a tool that isn't here yet? Tell Jordan what would help on
              your site — the library is built around what geologists actually
              ask for.
            </p>
            <span className="mt-auto inline-flex items-center justify-center gap-2 rounded-md border border-charcoal/15 bg-cream px-4 py-2.5 text-sm font-bold text-charcoal" data-id="element-645">
              Suggest a resource →
            </span>
          </motion.a>
        </div>
        <p className="mt-6 text-center text-xs text-warmgray" data-id="element-646">
          Resource concepts shown are in draft. Titles and availability may
          change as the library grows.
        </p>
      </div>
    </section>;
}